import PropTypes from 'prop-types';
import {useEffect} from 'react';

import GuidelineAnnotationDrugDetail from 'components/GuidelineAnnotation/DrugDetail';
import Loading from 'components/Loading';
import useSearchBar from 'components/Search/Bar/Provider';
import SideNav from 'components/SideNav';
import {getMenuItems, getTabComponent} from 'components/SideNav/utils';
import ErrorMessage from 'components/errors/ErrorMessage';
import KyError from 'components/errors/KyError';
import NotFound from 'components/shared/error/not_found';
import FullWidthResourceTemplate from 'components/templates/FullWidthResource';
import useAppContext from 'conf/AppContext';
import Types from 'conf/types';
import usePediatricContext from 'context/Pediatric';
import {useGet} from 'helpers/KyHooks';
import ChemicalOverviewTab from 'pages/Chemical/OverviewTab';


const propTypes = {
  id: PropTypes.string.isRequired,
  section: PropTypes.string,
  sectionId: PropTypes.string,
  loadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
/**
 * Renders chemical page.
 */
export default function ChemicalPage({id, section = 'overview', sectionId, loadId = 0, gsi}) {
  const appContext = useAppContext();
  const pediatricContext = usePediatricContext();
  const searchBar = useSearchBar();
  const showOverview = !section || section === 'overview';

  const {response, error} = useGet(`site/chemical/${id}`, {
    cacheResponse: !appContext.isPreview,
  }, {
    loadId,
  });
  useEffect(() => {
    if (response) {
      searchBar.initializeSearchBar([{id, objCls: 'chemical', name: response.data.chemical.name}]);
    }
  }, [response]);

  let name = `Drug ${id}`;
  let body = <Loading />;
  let counts = {};
  if (error) {
    if (error.response?.status === 404) {
      return <NotFound />;
    }
    body = <KyError kyError={error} />;

  } else if (response) {
    const {chemical, needsApproval, isPediatric, images, description, pedSummary, pgxSummary, indication, vips,
      classChildren, classParents, metaboliteOf, mixtures, formula, molWeightAvg, molWeightMono, targets,
      labelOccurrences} = response.data;
    counts = response.data.counts ?? {};
    name = chemical.name;

    if (showOverview) {
      body = (
        <ChemicalOverviewTab
          chemical={chemical}
          counts={counts}
          needsApproval={needsApproval}
          isPediatric={isPediatric}
          images={images}
          description={description}
          pedSummary={pedSummary}
          pgxSummary={pgxSummary}
          indication={indication}
          vips={vips}
          classChildren={classChildren}
          classParents={classParents}
          metaboliteOf={metaboliteOf}
          mixtures={mixtures}
          formula={formula}
          molWeightAvg={molWeightAvg}
          molWeightMono={molWeightMono}
          targets={targets}
          labelOccurrences={labelOccurrences}
        />
      );
    }
  }
  const Comp = getTabComponent(section, pediatricContext.isPediatricMode);
  if (Comp) {
    body = <Comp id={id} objCls="chemical" name={name} />;
  } else if (section === 'guidelineAnnotation') {
    body = <GuidelineAnnotationDrugDetail drugId={id} guidelineId={sectionId} gsi={gsi} />;
  } else if (!showOverview) {
    body = <ErrorMessage message={`Unsupported tab "${section}"`} />;
  }


  const baseUrl = Types.chemical.url(id);
  const menuItems = getMenuItems('chemical', baseUrl, counts);

  return (
    <FullWidthResourceTemplate id={id} type="chemical" name={name}>
      <SideNav items={menuItems}>
        {body}
      </SideNav>
    </FullWidthResourceTemplate>
  );
}
ChemicalPage.propTypes = propTypes;
